import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useHistory, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import CTAButton from "components/common/CTAButton";
import CurrencySelector from "components/common/CurrencySelector";
import CategoryAutocomplete from "components/pages/Tournament/CategoryAutocomplete";
import LeagueTournamentWizardFields from "components/pages/Tournament/LeagueTournamentWizardFields";
import AlertDialog from "components/common/AlertDialog";
import sports from "constants/sports";
import { TOURNAMENT_PENDING } from "constants/tournamentStates";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useCompetitionAdminRoutes } from "hooks/useCompetitionAdminRoutes";
import { buildCompetitionAdminRoutes } from "constants/competition-admin-routes";
import performRequest from "network/perform-request";
import {
  categoriesForTournamentsRoute,
  deleteTournamentRoute,
  leagueCustomerFeaturesRoute,
  leagueCustomerTeamsRoute,
  leagueCustomerTournamentsRoute,
  leagueTournamentKnockoutRoute,
  leagueTournamentLineupsRoute,
  restartTournamentRoute,
  tournamentsCreateRoute,
  tournamentsUpdateRoute,
} from "network/api-routes";
import { parseLeaguePaginatedResponse } from "components/pages/Gestion/gestion-pagination";
import EntryFeeTemplateSelect from "./EntryFeeTemplateSelect";
import {
  TEAM_MODE,
  TOURNAMENT_NAME_MAX_LENGTH,
  appendLeagueFormData,
  buildLeagueCreatePayload,
  canAdvanceFromTeams,
  canConfirmTeamList,
  clearWizardDraft,
  defaultAdvancePerGroup,
  lineupTeamLabel,
  lockedCurrencyFromFeatures,
  readWizardDraft,
  startDateBounds,
  startDateErrorId,
  tournamentNameErrorId,
  visibleCreateSteps,
  wizardDraftHasContent,
  wizardDraftKey,
  writeWizardDraft,
  yearFromStartAt,
} from "./gestion-league-wizard";

const sportFromTournament = (tournament) => {
  if (!tournament) return null;
  return (
    sports.find((item) => item.slug === tournament.sport_slug) ||
    sports.find((item) => item.sport_id === tournament.sport_id) ||
    sports.find((item) => item.slug === tournament.sport?.slug) ||
    null
  );
};

const GestionLeagueTournamentWizard = ({ tournament = null, competition = null, onSubmitResponse = null }) => {
  const intl = useIntl();
  const { user } = useContext(UserContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { id, customerId: routeCustomerId } = useParams();
  const competitionUuid = competition?.uuid_ || competition?.data?.uuid_ || id;
  const routes = useCompetitionAdminRoutes(competitionUuid);
  const gestionCustomerId = routeCustomerId || routes.customerId || competition?.customer_id;
  const isEdit = Boolean(tournament);
  const tournamentHasStarted = Boolean(tournament && tournament.state !== TOURNAMENT_PENDING);

  const draftKey = isEdit ? null : wizardDraftKey(competitionUuid || gestionCustomerId);
  // Read once: a draft left by Back or a reload restores the wizard where the user was.
  const [initialDraft] = useState(() => {
    const draft = readWizardDraft(draftKey);
    return wizardDraftHasContent(draft) ? draft : null;
  });
  const [draftRestored, setDraftRestored] = useState(Boolean(initialDraft));

  const [step, setStep] = useState(initialDraft?.step || "identity");
  const [teamMode, setTeamMode] = useState(initialDraft?.teamMode || "");
  const [copyFromId, setCopyFromId] = useState(initialDraft?.copyFromId || "");
  const [selectedTeams, setSelectedTeams] = useState(initialDraft?.selectedTeams || []);
  const [listConfirmed, setListConfirmed] = useState(Boolean(initialDraft?.listConfirmed));
  const [categories, setCategories] = useState([]);
  const [categoryPolicy, setCategoryPolicy] = useState({
    custom_category_allowed: true,
    official_category_required: false,
  });
  const [selectedSport, setSelectedSport] = useState(
    () =>
      sportFromTournament(tournament) ||
      (initialDraft?.formData?.sport_slug ? sportFromTournament({ sport_slug: initialDraft.formData.sport_slug }) : null),
  );
  const [selectedCategory, setSelectedCategory] = useState(
    tournament?.category?.data || tournament?.category || initialDraft?.selectedCategory || null,
  );
  const [startDate, setStartDate] = useState(() => {
    const startAt = tournament?.start_at || initialDraft?.formData?.start_at;
    return startAt ? dayjs(startAt) : null;
  });
  const [lockedCurrency, setLockedCurrency] = useState(null);
  const [leagueTournaments, setLeagueTournaments] = useState([]);
  const [rosterTeams, setRosterTeams] = useState([]);
  const [copiedLineups, setCopiedLineups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");
  // What the final phase looks like now (matches, results), to warn before a rebuild and to lock the fields while it has results.
  const [knockoutStatus, setKnockoutStatus] = useState(null);
  const [knockoutConfirmOpen, setKnockoutConfirmOpen] = useState(false);
  const [formData, setFormData] = useState(() => ({
    name: tournament?.name || "",
    sport_slug: tournament?.sport_slug || selectedSport?.slug || null,
    category_id: tournament?.category_id || tournament?.category?.data?.id || tournament?.category?.id || null,
    start_at: tournament?.start_at || null,
    entry_fee_template_id: tournament?.entry_fee_template_id ?? "",
    currency_code: tournament?.currency_code || competition?.currency_code || "USD",
    generate_fixtures: true,
    groups_enabled: true,
    groups_count: 1,
    teams_per_group: 4,
    advance_per_group: Number(tournament?.league_scaffold?.advance_per_group) || 0,
    // "Ida y vuelta" is off by default (D5); an existing tournament keeps its own setting.
    group_home_and_away: Boolean(tournament?.is_home_and_away),
    is_home_and_away: Boolean(tournament?.is_home_and_away),
    // Editing starts from the tournament's saved knockout setup (X-02 lets it change until a knockout match exists).
    knockout_enabled: Boolean(tournament?.league_scaffold?.knockout_enabled),
    knockout_home_and_away: Boolean(tournament?.league_scaffold?.knockout_home_and_away),
    knockout_final_home_and_away: Boolean(tournament?.league_scaffold?.knockout_final_home_and_away),
    matchdays_count: "",
    ...(initialDraft?.formData || {}),
  }));

  const customerId = gestionCustomerId || competition?.customer_id;

  const handleEntryFeeChange = useCallback(
    (entryFeeTemplateId) => setFormData((prev) => ({ ...prev, entry_fee_template_id: entryFeeTemplateId })),
    [],
  );

  useEffect(() => {
    if (!draftKey) return;
    const draft = { step, teamMode, copyFromId, selectedTeams, listConfirmed, formData, selectedCategory };
    if (wizardDraftHasContent(draft)) writeWizardDraft(draftKey, draft);
  }, [copyFromId, draftKey, formData, listConfirmed, selectedCategory, selectedTeams, step, teamMode]);

  const editedTournamentId = isEdit ? tournament?.id : null;
  useEffect(() => {
    if (!editedTournamentId) return undefined;
    let active = true;
    performRequest("GET", leagueTournamentKnockoutRoute(editedTournamentId), {}, true, user)
      .then((response) => {
        if (active) setKnockoutStatus(response?.data || null);
      })
      .catch(() => {
        if (active) setKnockoutStatus(null);
      });
    return () => {
      active = false;
    };
  }, [editedTournamentId, user]);

  // Rebuild rules (TC-1): hand-made stages are never rebuilt; results block any change; otherwise a change rebuilds the empty bracket.
  const knockoutMatchCount = Number(knockoutStatus?.matches_count) || 0;
  const knockoutManual = (Number(knockoutStatus?.manual_matches_count) || 0) > 0;
  const knockoutResultsCount = Number(knockoutStatus?.matches_with_results_count) || 0;
  const knockoutBlockedByResults = isEdit && !knockoutManual && knockoutResultsCount > 0;
  const savedScaffold = tournament?.league_scaffold || {};
  const knockoutSettingsChanged =
    isEdit &&
    (Boolean(formData.knockout_enabled) !== Boolean(savedScaffold.knockout_enabled) ||
      (Number(formData.advance_per_group) || 0) !== (Number(savedScaffold.advance_per_group) || 0) ||
      Boolean(formData.knockout_home_and_away) !== Boolean(savedScaffold.knockout_home_and_away) ||
      Boolean(formData.knockout_final_home_and_away) !== Boolean(savedScaffold.knockout_final_home_and_away));
  const knockoutRebuildCount = knockoutSettingsChanged && !knockoutManual ? knockoutMatchCount : 0;

  const discardDraft = () => {
    clearWizardDraft(draftKey);
    setDraftRestored(false);
    setStep("identity");
    setTeamMode("");
    setCopyFromId("");
    setSelectedTeams([]);
    setListConfirmed(false);
    setSelectedSport(null);
    setSelectedCategory(null);
    setStartDate(null);
    setFormData((prev) => ({
      ...prev,
      name: "",
      sport_slug: null,
      category_id: null,
      new_category_name: null,
      new_category_modality: null,
      start_at: null,
      entry_fee_template_id: "",
    }));
  };
  const steps = visibleCreateSteps(teamMode);
  const stepIndex = Math.max(0, steps.indexOf(step));
  const previewTeams =
    teamMode === TEAM_MODE.COPY ? copiedLineups.map(lineupTeamLabel) : selectedTeams.map((team) => team.name);
  const year = yearFromStartAt(formData.start_at);

  const getCategories = useCallback(
    (sportSlug) => {
      if (!sportSlug) return;
      performRequest(
        "POST",
        categoriesForTournamentsRoute,
        {
          sport_slug: sportSlug,
          country: competition?.country || tournament?.competition?.data?.country,
          organization_id: competition?.organization?.id || competition?.organization_id,
        },
        true,
        user,
      )
        .then((response) => {
          setCategories(response.data.data || []);
          setCategoryPolicy(response.data.meta || { custom_category_allowed: true, official_category_required: false });
        })
        .catch(() => setCategories([]));
    },
    [competition, tournament, user],
  );

  useEffect(() => {
    if (formData.sport_slug) getCategories(formData.sport_slug);
  }, [formData.sport_slug, getCategories]);

  useEffect(() => {
    if (!customerId || !user) return;
    performRequest("GET", leagueCustomerFeaturesRoute(customerId), {}, true, user)
      .then((response) => {
        const locked = lockedCurrencyFromFeatures(response.data);
        setLockedCurrency(locked);
        if (locked) {
          setFormData((prev) => ({ ...prev, currency_code: locked }));
        }
      })
      .catch(() => setLockedCurrency(null));
  }, [customerId, user]);

  useEffect(() => {
    if (!customerId || !user || isEdit) return;
    performRequest("GET", leagueCustomerTournamentsRoute(customerId), { page_size: 100 }, true, user)
      .then((response) => {
        const results = parseLeaguePaginatedResponse(response.data).results || [];
        setLeagueTournaments(results.filter((item) => !item.archived));
      })
      .catch(() => setLeagueTournaments([]));
    performRequest(
      "GET",
      leagueCustomerTeamsRoute(customerId),
      { page: 0, page_size: 100, include_archived: false },
      true,
      user,
    )
      .then((response) => setRosterTeams(parseLeaguePaginatedResponse(response.data).results || []))
      .catch(() => setRosterTeams([]));
  }, [customerId, isEdit, user]);

  useEffect(() => {
    if (!copyFromId || !user) {
      setCopiedLineups([]);
      return;
    }
    performRequest("GET", leagueTournamentLineupsRoute(copyFromId), {}, true, user)
      .then((response) => setCopiedLineups(Array.isArray(response.data) ? response.data : []))
      .catch(() => setCopiedLineups([]));
  }, [copyFromId, user]);

  const handleTeamModeChange = (mode) => {
    setTeamMode(mode);
    setListConfirmed(false);
    if (mode !== TEAM_MODE.COPY) {
      setCopyFromId("");
      setCopiedLineups([]);
    }
    if (mode !== TEAM_MODE.PICK) {
      setSelectedTeams([]);
    }
    setFormData((prev) => ({ ...prev, generate_fixtures: mode !== TEAM_MODE.LATER }));
  };

  const nameErrorId = tournamentNameErrorId(formData.name);
  const dateErrorId = startDateErrorId(formData.start_at, { originalStartAt: tournament?.start_at });
  const dateBounds = startDateBounds();
  const identityValid = Boolean(
    formData.name?.trim() &&
      !nameErrorId &&
      !dateErrorId &&
      selectedSport &&
      (formData.category_id || formData.new_category_name) &&
      formData.start_at,
  );

  const canGoNext = useMemo(() => {
    if (step === "identity") return identityValid;
    if (step === "teams") {
      return canAdvanceFromTeams(teamMode, { copyFromId, teamIds: selectedTeams.map((team) => team.id) });
    }
    if (step === "confirm") {
      return canConfirmTeamList({ confirmed: listConfirmed, teamCount: previewTeams.length });
    }
    return true;
  }, [copyFromId, identityValid, listConfirmed, previewTeams.length, selectedTeams, step, teamMode]);

  const goNext = () => {
    const next = steps[stepIndex + 1];
    if (next) setStep(next);
  };

  const goBack = () => {
    const previous = steps[stepIndex - 1];
    if (previous) {
      if (previous === "teams") setListConfirmed(false);
      setStep(previous);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (!isEdit && step !== steps[steps.length - 1]) {
      if (canGoNext) goNext();
      return;
    }
    // Changing the final phase rebuilds its (still empty) bracket: ask first.
    if (knockoutRebuildCount > 0) {
      setKnockoutConfirmOpen(true);
      return;
    }
    saveTournament();
  };

  const saveTournament = () => {
    const headers = { "Content-Type": "multipart/form-data" };
    const body = new FormData();
    setSubmitting(true);

    if (isEdit) {
      Object.entries({
        tournament_id: tournament.id,
        name: formData.name,
        sport_slug: formData.sport_slug,
        category_id: formData.category_id,
        start_at: formData.start_at,
        year: yearFromStartAt(formData.start_at),
        currency_code: formData.currency_code,
        is_home_and_away: Boolean(formData.is_home_and_away),
        requires_document_for_players: false,
        knockout_enabled: formData.knockout_enabled ? 1 : 0,
        advance_per_group: Number(formData.advance_per_group) || 0,
        knockout_home_and_away: formData.knockout_home_and_away ? 1 : 0,
        knockout_final_home_and_away: formData.knockout_final_home_and_away ? 1 : 0,
      }).forEach(([key, value]) => body.append(key, value === null || value === undefined ? "" : value));
      if (formData.entry_fee_template_id !== "" && formData.entry_fee_template_id != null) {
        body.append("entry_fee_template_id", formData.entry_fee_template_id);
      }
    } else {
      appendLeagueFormData(
        body,
        buildLeagueCreatePayload({
          competitionUuid,
          form: formData,
          teamMode,
          copyFromId,
          teamIds: selectedTeams.map((team) => team.id),
          listConfirmed: teamMode === TEAM_MODE.LATER ? false : listConfirmed,
          teamCount: previewTeams.length,
        }),
      );
    }

    performRequest("POST", isEdit ? tournamentsUpdateRoute : tournamentsCreateRoute, body, true, user, null, headers)
      .then((response) => {
        setSubmitting(false);
        if (isEdit) {
          enqueueSnackbar(intl.formatMessage({ id: "Los cambios se guardaron correctamente" }), { variant: "success" });
          onSubmitResponse?.(response.data.data);
          return;
        }
        enqueueSnackbar(intl.formatMessage({ id: "Torneo creado con éxito" }), { variant: "success" });
        clearWizardDraft(draftKey);
        const createdUuid = response.data.data?.uuid_;
        if (gestionCustomerId && createdUuid) {
          history.push(GESTION_ROUTES.tournament(gestionCustomerId, createdUuid));
        } else {
          history.push(routes.tournaments);
        }
      })
      .catch((error) => {
        setSubmitting(false);
        const data = error?.response?.data;
        // knockout_has_results: the message names the matches; the matches must be reopened and cleared first.
        enqueueSnackbar(data?.message || intl.formatMessage({ id: "Lo sentimos, ha ocurrido un error" }), {
          variant: "error",
          ...(data?.code === "knockout_has_results" ? { autoHideDuration: 12000 } : {}),
        });
      });
  };

  const resetTournament = () => {
    performRequest("POST", restartTournamentRoute, { tournament_id: tournament.id }, true, user)
      .then(() => {
        onSubmitResponse?.(false);
        enqueueSnackbar(intl.formatMessage({ id: "Torneo reiniciado con éxito" }), { variant: "success" });
      })
      .catch((error) => {
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "Lo sentimos, ha ocurrido un error" }),
          { variant: "error" },
        );
      });
  };

  const confirmDeleteTournament = () => {
    const competitionUUID = tournament.competition?.data?.uuid_;
    performRequest("POST", deleteTournamentRoute, { tournament_id: tournament.id }, true, user)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "Torneo eliminado correctamente" }), { variant: "success" });
        if (gestionCustomerId) {
          history.push(GESTION_ROUTES.tournaments(gestionCustomerId));
        } else if (competitionUUID) {
          history.push(buildCompetitionAdminRoutes(competitionUUID, routes.customerId).tournaments);
        } else {
          history.push("/");
        }
      })
      .catch((error) => {
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "Lo sentimos, ha ocurrido un error" }),
          { variant: "error" },
        );
      });
  };

  const identityFields = (
    <Stack gap={3}>
      <TextField
        required
        fullWidth
        value={formData.name}
        label={intl.formatMessage({ id: "Nombre del torneo" })}
        onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
        error={Boolean(nameErrorId)}
        helperText={
          nameErrorId
            ? intl.formatMessage({ id: nameErrorId }, { max: TOURNAMENT_NAME_MAX_LENGTH })
            : `${(formData.name || "").length}/${TOURNAMENT_NAME_MAX_LENGTH}`
        }
        inputProps={{ maxLength: TOURNAMENT_NAME_MAX_LENGTH, "data-testid": "wizard-name" }}
      />
      <Stack sx={{ flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
        <Autocomplete
          fullWidth
          value={selectedSport}
          options={sports}
          disabled={isEdit && tournamentHasStarted}
          getOptionLabel={(sport) => intl.formatMessage({ id: sport.name })}
          onChange={(_event, value) => {
            setSelectedSport(value);
            setSelectedCategory(null);
            setFormData((prev) => ({
              ...prev,
              sport_slug: value?.slug || null,
              category_id: null,
              new_category_name: null,
            }));
            if (value) getCategories(value.slug);
          }}
          renderInput={(params) => (
            <TextField {...params} required label={intl.formatMessage({ id: "Seleccione el deporte del torneo" })} />
          )}
        />
        <CategoryAutocomplete
          categories={categories}
          disabled={!selectedSport}
          selectedCategory={selectedCategory}
          customCategoryAllowed={categoryPolicy.custom_category_allowed}
          officialCategoryRequired={categoryPolicy.official_category_required}
          onCategorySelected={(category) => {
            setSelectedCategory(category);
            setFormData((prev) => ({
              ...prev,
              category_id: category?.id || null,
              new_category_name: category?.id ? null : category?.name || null,
              new_category_modality: category?.id ? null : category?.modality || null,
            }));
          }}
        />
        <DatePicker
          sx={{ width: "100%" }}
          label={intl.formatMessage({ id: "Fecha de inicio" })}
          value={startDate}
          onChange={(date) => {
            setStartDate(date);
            setFormData((prev) => ({
              ...prev,
              start_at: date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : null,
            }));
          }}
          minDate={isEdit ? undefined : dateBounds.min}
          maxDate={isEdit ? undefined : dateBounds.max}
          slotProps={{
            textField: {
              required: true,
              error: Boolean(dateErrorId),
              helperText: dateErrorId ? intl.formatMessage({ id: dateErrorId }) : undefined,
            },
          }}
        />
      </Stack>
      {year ? (
        <RevaText variant="body2" color="text.secondary">
          {intl.formatMessage({ id: "gestion.leagueWizard.yearFromStart" }, { year })}
        </RevaText>
      ) : null}
      <Stack sx={{ flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
        {customerId ? (
          <Box sx={{ flex: 1 }}>
            <EntryFeeTemplateSelect
              customerId={customerId}
              tournamentId={isEdit ? tournament?.id : null}
              value={formData.entry_fee_template_id ?? ""}
              onChange={handleEntryFeeChange}
            />
          </Box>
        ) : null}
        <CurrencySelector
          required
          value={formData.currency_code}
          disabled={Boolean(lockedCurrency)}
          onChange={(_event, value) => {
            if (lockedCurrency) return;
            setFormData((prev) => ({ ...prev, currency_code: value }));
          }}
        />
      </Stack>
      {lockedCurrency ? (
        <Alert severity="info">
          {intl.formatMessage({ id: "gestion.leagueWizard.currencyLocked" }, { currency: lockedCurrency })}
        </Alert>
      ) : null}
    </Stack>
  );

  return (
    <form onSubmit={submit}>
      <Stack sx={{ gap: 3 }}>
        {!isEdit ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
            <RevaText variant="h4">{intl.formatMessage({ id: "gestion.leagueWizard.title" })}</RevaText>
            {gestionCustomerId ? (
              <Button
                type="button"
                variant="outlined"
                onClick={() => history.push(GESTION_ROUTES.tournaments(gestionCustomerId))}
              >
                {intl.formatMessage({ id: "gestion.leagueWizard.backToList" })}
              </Button>
            ) : null}
          </Stack>
        ) : null}

        {draftRestored ? (
          <Alert
            data-testid="wizard-draft-restored"
            severity="info"
            onClose={() => setDraftRestored(false)}
            action={
              <Button color="inherit" size="small" onClick={discardDraft}>
                {intl.formatMessage({ id: "gestion.leagueWizard.discardDraft" })}
              </Button>
            }
          >
            {intl.formatMessage({ id: "gestion.leagueWizard.draftRestored" })}
          </Alert>
        ) : null}

        {!isEdit ? (
          <Stepper activeStep={stepIndex} alternativeLabel>
            {steps.map((item) => (
              <Step key={item}>
                <StepLabel>{intl.formatMessage({ id: `gestion.leagueWizard.step.${item}` })}</StepLabel>
              </Step>
            ))}
          </Stepper>
        ) : null}

        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          {isEdit || step === "identity" ? identityFields : null}

          {isEdit ? (
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formData.is_home_and_away)}
                    disabled={tournamentHasStarted}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_home_and_away: event.target.checked,
                        group_home_and_away: event.target.checked,
                      }))
                    }
                  />
                }
                label={intl.formatMessage({ id: "league.wizard.homeAndAway" })}
              />
              <Stack gap={1} sx={{ mt: 2 }} data-testid="edit-knockout">
                <RevaText variant="subtitle1">{intl.formatMessage({ id: "league.wizard.scaffoldKnockout" })}</RevaText>
                <RevaText variant="caption" color="text.secondary">
                  {intl.formatMessage({ id: "gestion.leagueWizard.knockoutEditHint" })}
                </RevaText>
                {knockoutBlockedByResults ? (
                  <Alert severity="warning" data-testid="knockout-has-results">
                    {intl.formatMessage({ id: "gestion.leagueWizard.knockoutHasResults" }, { count: knockoutResultsCount })}
                  </Alert>
                ) : null}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formData.knockout_enabled)}
                      disabled={knockoutBlockedByResults}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          knockout_enabled: checked,
                          advance_per_group: checked && !(prev.advance_per_group > 0) ? defaultAdvancePerGroup() : prev.advance_per_group,
                        }));
                      }}
                    />
                  }
                  label={intl.formatMessage({ id: "league.wizard.knockoutEnabled" })}
                />
                {formData.knockout_enabled ? (
                  <Stack gap={1} sx={{ pl: 4 }}>
                    <TextField
                      type="number"
                      size="small"
                      sx={{ maxWidth: 280 }}
                      label={intl.formatMessage({ id: "league.wizard.advancePerGroup" })}
                      value={formData.advance_per_group ?? ""}
                      inputProps={{ min: 1 }}
                      disabled={knockoutBlockedByResults}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          advance_per_group: event.target.value === "" ? "" : Number(event.target.value),
                        }))
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formData.knockout_home_and_away)}
                          disabled={knockoutBlockedByResults}
                          onChange={(event) =>
                            setFormData((prev) => ({ ...prev, knockout_home_and_away: event.target.checked }))
                          }
                        />
                      }
                      label={intl.formatMessage({ id: "league.wizard.knockoutHomeAndAway" })}
                    />
                    {formData.knockout_home_and_away ? (
                      <FormControlLabel
                        sx={{ pl: 4 }}
                        control={
                          <Checkbox
                            checked={Boolean(formData.knockout_final_home_and_away)}
                            disabled={knockoutBlockedByResults}
                            onChange={(event) =>
                              setFormData((prev) => ({ ...prev, knockout_final_home_and_away: event.target.checked }))
                            }
                          />
                        }
                        label={intl.formatMessage({ id: "league.wizard.knockoutFinalHomeAndAway" })}
                      />
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </Box>
          ) : null}

          {!isEdit && step === "teams" ? (
            <Stack gap={2}>
              <RevaText variant="h6">{intl.formatMessage({ id: "gestion.leagueWizard.teamsTitle" })}</RevaText>
              <RadioGroup value={teamMode} onChange={(event) => handleTeamModeChange(event.target.value)}>
                <FormControlLabel
                  value={TEAM_MODE.COPY}
                  control={<Radio />}
                  label={intl.formatMessage({ id: "gestion.leagueWizard.teamModeCopy" })}
                />
                <FormControlLabel
                  value={TEAM_MODE.PICK}
                  control={<Radio />}
                  label={intl.formatMessage({ id: "gestion.leagueWizard.teamModePick" })}
                />
                <FormControlLabel
                  value={TEAM_MODE.LATER}
                  control={<Radio />}
                  label={intl.formatMessage({ id: "gestion.leagueWizard.teamModeLater" })}
                />
              </RadioGroup>
              {teamMode === TEAM_MODE.COPY ? (
                <TextField
                  select
                  fullWidth
                  label={intl.formatMessage({ id: "league.wizard.copyLineups" })}
                  value={copyFromId}
                  onChange={(event) => {
                    setCopyFromId(event.target.value);
                    setListConfirmed(false);
                  }}
                >
                  {leagueTournaments.map((item) => (
                    <MenuItem key={item.revaId || item.id} value={item.revaId || item.id}>
                      {item.name} {item.year ? `(${item.year})` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              {teamMode === TEAM_MODE.PICK ? (
                <Autocomplete
                  multiple
                  options={rosterTeams}
                  value={selectedTeams}
                  filterSelectedOptions
                  getOptionLabel={(team) => team.name || `#${team.id}`}
                  isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                  onChange={(_event, value) => {
                    setSelectedTeams(value);
                    setListConfirmed(false);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={intl.formatMessage({ id: "gestion.leagueWizard.pickTeams" })} />
                  )}
                />
              ) : null}
              {teamMode === TEAM_MODE.LATER ? (
                <Alert severity="info">{intl.formatMessage({ id: "gestion.leagueWizard.laterHint" })}</Alert>
              ) : null}
            </Stack>
          ) : null}

          {!isEdit && step === "confirm" ? (
            <Stack gap={2}>
              <RevaText variant="h6">{intl.formatMessage({ id: "gestion.leagueWizard.confirmTitle" })}</RevaText>
              {previewTeams.length === 0 ? (
                <Alert severity="warning">{intl.formatMessage({ id: "gestion.leagueWizard.confirmEmpty" })}</Alert>
              ) : (
                <Stack component="ul" sx={{ pl: 2, m: 0 }}>
                  {previewTeams.map((name, index) => (
                    <li key={`${name}-${index}`}>{name}</li>
                  ))}
                </Stack>
              )}
              <FormControlLabel
                control={
                  <Checkbox checked={listConfirmed} onChange={(event) => setListConfirmed(event.target.checked)} />
                }
                label={intl.formatMessage({ id: "gestion.leagueWizard.confirmCheckbox" }, { count: previewTeams.length })}
              />
            </Stack>
          ) : null}

          {!isEdit && step === "fixtures" ? (
            <LeagueTournamentWizardFields
              formData={formData}
              setFormData={setFormData}
              leagueTournaments={[]}
              tournamentHasStarted={false}
              isEdit={false}
              hideYear
              hideCopy
              hideSetupMode
              teamCount={previewTeams.length}
            />
          ) : null}
        </Paper>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {isEdit ? (
            <>
              <Button color="warning" variant="outlined" onClick={() => setRestartDialogOpen(true)}>
                {intl.formatMessage({ id: "Reiniciar torneo" })}
              </Button>
              <Button color="error" variant="outlined" onClick={() => setDeleteDialogOpen(true)}>
                {intl.formatMessage({ id: "Eliminar torneo" })}
              </Button>
              <CTAButton type="submit" disabled={submitting || !identityValid}>
                {intl.formatMessage({ id: "Guardar cambios" })}
              </CTAButton>
            </>
          ) : (
            <>
              {stepIndex > 0 ? (
                <Button variant="outlined" color="primary" onClick={goBack}>
                  {intl.formatMessage({ id: "gestion.leagueWizard.back" })}
                </Button>
              ) : null}
              {stepIndex < steps.length - 1 ? (
                <Button variant="contained" color="primary" onClick={goNext} disabled={!canGoNext}>
                  {intl.formatMessage({ id: "gestion.leagueWizard.next" })}
                </Button>
              ) : (
                <CTAButton type="submit" disabled={submitting || !canGoNext}>
                  {intl.formatMessage({ id: "gestion.leagueWizard.create" })}
                </CTAButton>
              )}
            </>
          )}
        </Stack>
      </Stack>

      {isEdit ? (
        <>
          <AlertDialog
            hideButton
            isOpen={knockoutConfirmOpen}
            onConfirm={() => {
              setKnockoutConfirmOpen(false);
              saveTournament();
            }}
            onCancel={() => setKnockoutConfirmOpen(false)}
            alertTitle={intl.formatMessage({ id: "gestion.leagueWizard.knockoutRebuildTitle" })}
            alertBody={intl.formatMessage(
              { id: "gestion.leagueWizard.knockoutRebuildBody" },
              { count: knockoutRebuildCount },
            )}
            confirmButtonTitle={intl.formatMessage({ id: "gestion.leagueWizard.knockoutRebuildConfirm" })}
            cancelButtonTitle={intl.formatMessage({ id: "Cancelar" })}
          />
          <AlertDialog
            hideButton
            isOpen={restartDialogOpen}
            onConfirm={() => {
              setRestartDialogOpen(false);
              resetTournament();
            }}
            onCancel={() => setRestartDialogOpen(false)}
            alertTitle={intl.formatMessage({ id: "Reiniciar torneo" })}
            alertBody={intl.formatMessage({
              id: "Se eliminarán los partidos y resultados. Esta acción no se puede deshacer.",
            })}
            confirmButtonTitle={intl.formatMessage({ id: "Reiniciar torneo" })}
            cancelButtonTitle={intl.formatMessage({ id: "Cancelar" })}
          />
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1}>
                <WarningAmberIcon color="error" />
                <FormattedMessage id="Confirmar eliminación del torneo" />
              </Box>
            </DialogTitle>
            <DialogContent>
              <RevaText id="delete_tournament_confirmation" values={{ strong: (chunks) => <strong>{chunks}</strong> }} />
              <TextField
                fullWidth
                label={intl.formatMessage({ id: "Escribe ELIMINAR para confirmar" })}
                value={confirmationInput}
                onChange={(event) => setConfirmationInput(event.target.value)}
                sx={{ mt: 2 }}
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button color="text.secondary" onClick={() => setDeleteDialogOpen(false)}>
                <FormattedMessage id="Cancelar" />
              </Button>
              <Button
                color="error"
                disabled={confirmationInput !== intl.formatMessage({ id: "ELIMINAR" })}
                onClick={confirmDeleteTournament}
              >
                <FormattedMessage id="Eliminar torneo" />
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}
    </form>
  );
};

export default GestionLeagueTournamentWizard;
